import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Image as ImageLucide,
  ImageIcon,
  LayoutGrid,
  Lock,
  List as ListIcon,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  FileText,
} from "lucide-react";

import Spinner from "../../../Layouts/Spinner";
import FormField from "../../../Common/FormField/FormField";
import LandingPageSeoSection from "../../../Common/LandingPageSeo/LandingPageSeoSection";
import PagePreview from "../../../Common/LandingPageSeo/PagePreview";
import { crumbPath, metaDescriptionFallback, pageAddress } from "../../../Common/LandingPageSeo/seoPreview";
import { landingPageValues, splitLandingPageValues } from "../../../Common/LandingPageSeo/landingPageFields";
import { handleImageUpload } from "../../../Common/ImageUpload/handleImageUpload";
import { nbSelectStyles } from "../../../Common/FormShell/nbSelectStyles";
import { addBrandValidation } from "../../../Constant/constant";
import {
  addBrand,
  getSingleBrand,
  updateOneBrand,
} from "../../../../redux/brand/action";
import UnsavedChangesGuard from "../../Category/sections/UnsavedChangesGuard";
import {
  CATEGORY_STATUS,
  POSITION_MAX,
  POSITION_MIN,
} from "../../Category/constants";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import axiosInstance from "../../../../redux/api";
import "../../../Common/FormShell/FormShell.css";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_SIZE_MB = 5;

const PREVIEW_TABS = [
  { key: "storefront", label: "Storefront", Icon: LayoutGrid },
  { key: "page", label: "Page", Icon: FileText },
  { key: "listing", label: "Admin list", Icon: ListIcon },
  { key: "search", label: "Search", Icon: Search },
];

const BANNER_TYPE_OPTIONS = [
  { value: 1, label: "Category" },
  { value: 2, label: "Occasion" },
  { value: 3, label: "Brand" },
];

const INITIAL_VALUES = {
  name: "",
  ar_name: "",
  image: "",
  status: CATEGORY_STATUS.ACTIVE,
  position: 1,
  home_view: false,
  // The SEO section edits the brand's storefront page; see landingPageFields.
  ...landingPageValues(null),
  banner_image: "",
  ar_banner_image: "",
  banner_type: null,
  banner_master_id: "",
};

const slugify = (str = "") =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const BrandForm = ({ mode = "create" }) => {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [submitting, setSubmitting] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewTab, setPreviewTab] = useState("storefront");
  const fileInputRef = useRef(null);

  // Banner-only dropdown options (edit mode only)
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [occasionOptions, setOccasionOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  const singleBrand = useSelector(
    (state) => state.reusableSlice.sinlgeBrand,
  );
  const isEditable = isEdit
    ? hasAction(MENU.CATALOGUE, PERMISSION.EDIT)
    : true;
  const readOnly = isEdit && !isEditable;

  // Fetch the brand on mount in edit mode.
  useEffect(() => {
    if (isEdit && id) dispatch(getSingleBrand(id));
  }, [dispatch, id, isEdit]);

  // Banner master_id source data — only needed in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const [catRes, occRes, brandRes] = await Promise.all([
          axiosInstance.post("/category/get_all", { page: "" }),
          axiosInstance.post("/occasion/get_all", { page: "" }),
          axiosInstance.post("/brand/get_all", { page: "" }),
        ]);
        if (cancelled) return;
        setCategoryOptions(
          (catRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.title,
          })),
        );
        setOccasionOptions(
          (occRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.name,
          })),
        );
        setBrandOptions(
          (brandRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.name,
          })),
        );
      } catch (err) {
        if (!cancelled) console.error("Banner option fetch failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  // The store keeps the last brand opened until this one arrives; seed only
  // once it is this brand, so one brand's page copy is never shown (or
  // saved) on another's form.
  const recordMatches =
    !!singleBrand && Number(singleBrand.id) === Number(id);
  const initialValues = useMemo(() => {
    if (!isEdit || !recordMatches) return INITIAL_VALUES;
    return {
      name: singleBrand?.name || "",
      ar_name: singleBrand?.ar_name || "",
      image: singleBrand?.image || "",
      status: singleBrand?.status || CATEGORY_STATUS.ACTIVE,
      position: singleBrand?.position || 1,
      home_view:
        singleBrand?.home_view === true || singleBrand?.home_view === "true",
      ...landingPageValues(singleBrand?.landing_page),
      banner_image: singleBrand?.banner_image || "",
      ar_banner_image: singleBrand?.ar_banner_image || "",
      banner_type: singleBrand?.banner_type || null,
      banner_master_id: singleBrand?.banner_master_id || "",
    };
  }, [isEdit, recordMatches, singleBrand]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => addBrandValidation, []),
    onSubmit: (values) => {
      setSubmitting(true);
      if (isEdit) {
        // The SEO copy goes to the brand's page, as `landing_page`.
        const payload = {
          ...splitLandingPageValues(values),
          id: singleBrand?.id ?? Number(id),
        };
        dispatch(updateOneBrand(payload, setSubmitting, navigate));
      } else {
        dispatch(
          addBrand(splitLandingPageValues(values), setSubmitting, navigate),
        );
      }
    },
  });

  const { values, errors, touched } = formik;
  const isDirty = formik.dirty && !submitting;
  const errorCount = Object.keys(errors).filter((k) => touched[k]).length;
  const imageUrl = values.image;
  const imageError = touched.image && errors.image;
  const isActive = Number(values.status) === CATEGORY_STATUS.ACTIVE;

  const previewName = (values.name || "").trim() || (isEdit ? singleBrand?.name : "Brand name") || "Brand name";
  const previewSlug = slugify(values.name) || "brand-slug";
  const previewArName = (values.ar_name || "").trim();
  const landingPage = (recordMatches && singleBrand?.landing_page) || null;
  const previewMetaTitle =
    (values.lp_seo_title || "").trim() || `${previewName} | Nysmah`;
  const previewMetaDesc =
    (values.lp_meta_description || "").trim() || metaDescriptionFallback("en", previewName);
  const previewPath = pageAddress("en", landingPage, `brands/${previewSlug}`);

  // Pick banner_master_id options based on chosen banner_type
  const bannerMasterOptions =
    values.banner_type === 1
      ? categoryOptions
      : values.banner_type === 2
      ? occasionOptions
      : values.banner_type === 3
      ? brandOptions
      : [];

  const processFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError("image", `Image must be under ${MAX_SIZE_MB}MB`);
      formik.setFieldTouched("image", true, false);
      return;
    }
    setUploading(true);
    try {
      await handleImageUpload(
        file,
        { title: "images", name: "image" },
        "image",
        formik,
      );
    } catch {
      formik.setFieldError("image", "Image upload failed. Try again.");
      formik.setFieldTouched("image", true, false);
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = "";
  };
  const onTileClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  const onDiscard = () => {
    if (
      isDirty &&
      !window.confirm(
        t("You have unsaved changes. Leave this page and discard them?"),
      )
    ) {
      return;
    }
    navigate("/brand");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    const errs = await formik.validateForm();
    formik.setTouched(
      Object.keys(errs).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    );
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
      return;
    }
    formik.handleSubmit(e);
  };

  // Inline banner upload handler — used by both banner image fields.
  const uploadBannerField = async (file, fieldName) => {
    if (!file || readOnly) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError(fieldName, `Image must be under ${MAX_SIZE_MB}MB`);
      return;
    }
    try {
      await handleImageUpload(
        file,
        { title: "images", name: fieldName },
        fieldName,
        formik,
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="nb-page">
      <UnsavedChangesGuard when={isDirty} />

      {/* Crumb bar */}
      <div className="nb-topbar">
        <Link to="/brand" className="nb-topbar__back">
          <ChevronLeft size={14} aria-hidden="true" />
          {t("Brands")}
        </Link>
        <ChevronRight size={12} className="nb-topbar__sep" aria-hidden="true" />
        <span className="nb-topbar__current">
          {isEdit ? singleBrand?.name || t("Edit brand") : t("New brand")}
        </span>
        {readOnly ? (
          <span
            className="nb-topbar__draft"
            style={{
              color: "var(--nb-muted)",
              background: "#f1f5f9",
              borderColor: "var(--nb-border-strong)",
            }}
          >
            <Lock size={11} aria-hidden="true" />
            {t("Read only")}
          </span>
        ) : (
          isDirty && (
            <span className="nb-topbar__draft">
              <span className="nb-topbar__dot" aria-hidden="true" />
              {isEdit ? t("Edited · unsaved") : t("Draft · unsaved")}
            </span>
          )
        )}
      </div>

      <form className="nb-shell" onSubmit={handleSubmit} noValidate>
        {/* Left: form */}
        <div className="nb-form">
          <header className="nb-hero">
            <span className="nb-hero__eyebrow">
              <Sparkles size={12} aria-hidden="true" />
              {t("Catalog")}
            </span>
            <h1 className="nb-hero__title">
              {isEdit ? t("Edit brand") : t("Create a new brand")}
            </h1>
            <p className="nb-hero__sub">
              {t(
                "Brands group products under a single identity on your storefront and in search.",
              )}
            </p>
          </header>

          {errorCount > 0 && (
            <div className="nb-banner" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                {errorCount === 1
                  ? t("1 field needs your attention.")
                  : t(`${errorCount} fields need your attention.`)}
              </span>
            </div>
          )}

          {/* 01 Identity */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">01</span>
              <div>
                <h2 className="nb-section__title">{t("Identity")}</h2>
                <p className="nb-section__hint">
                  {t("Bilingual brand name shown on the storefront and search.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="name"
                  label={t("Brand name (English)")}
                  required
                  touched={touched.name}
                  error={errors.name}
                >
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className="nb-input"
                    placeholder={t("e.g. Givenchy")}
                    value={values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
                <FormField
                  name="ar_name"
                  label={t("Brand name (Arabic)")}
                  touched={touched.ar_name}
                  error={errors.ar_name}
                >
                  <input
                    id="ar_name"
                    name="ar_name"
                    type="text"
                    className="nb-input"
                    placeholder={t("الاسم بالعربية")}
                    dir="rtl"
                    lang="ar"
                    value={values.ar_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
              </div>
            </div>
          </section>

          {/* 02 Logo */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">02</span>
              <div>
                <h2 className="nb-section__title">{t("Logo")}</h2>
                <p className="nb-section__hint">
                  {t("Square image works best — shown on the storefront card and admin list.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div
                className={`nb-drop ${dragOver ? "is-drag" : ""} ${
                  imageError ? "is-error" : ""
                } ${imageUrl ? "has-image" : ""}`}
                onClick={onTileClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTileClick();
                  }
                }}
                onDrop={onDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!readOnly) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                role="button"
                tabIndex={readOnly ? -1 : 0}
                aria-label={t("Upload brand image")}
                aria-disabled={readOnly}
              >
                {imageUrl ? (
                  <>
                    <img src={imageUrl} alt={t("Brand logo")} className="nb-drop__img" />
                    {!readOnly && (
                      <div className="nb-drop__overlay">
                        <Upload size={18} aria-hidden="true" />
                        <span>{t("Replace image")}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="nb-drop__empty">
                    <span className="nb-drop__bubble">
                      {uploading ? <Spinner /> : <ImageIcon size={20} aria-hidden="true" />}
                    </span>
                    <div className="nb-drop__title">
                      {uploading ? t("Uploading…") : t("Drop a logo or click to upload")}
                    </div>
                    <div className="nb-drop__hint">
                      {t("PNG, JPG, WEBP or SVG · up to 5MB")}
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  id="image"
                  name="image"
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFile}
                  hidden
                  disabled={uploading || readOnly}
                />
              </div>
              {imageUrl && !readOnly && (
                <div className="nb-thumbrow">
                  <button type="button" className="nb-btn nb-btn--ghost nb-btn--sm" onClick={onTileClick}>
                    <Upload size={13} aria-hidden="true" />
                    {t("Replace")}
                  </button>
                  <button
                    type="button"
                    className="nb-btn nb-btn--danger-ghost nb-btn--sm"
                    onClick={() => formik.setFieldValue("image", "")}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                    {t("Remove")}
                  </button>
                </div>
              )}
              {imageError && (
                <p className="nb-fielderror" role="alert">
                  {t(errors.image)}
                </p>
              )}
            </div>
          </section>

          {/* 03 Banner — edit mode only */}
          {isEdit && (
            <section className={`nb-section nb-section--seo ${bannerOpen ? "is-open" : ""}`}>
              <div className="nb-section__head">
                <span className="nb-section__step">03</span>
                <div>
                  <h2 className="nb-section__title">
                    <ImageLucide size={14} aria-hidden="true" style={{ marginRight: 6, opacity: 0.7 }} />
                    {t("Hero banner")}
                    <span className="nb-section__optional">{t("Optional")}</span>
                  </h2>
                  <p className="nb-section__hint">
                    {t("Wide banner shown at the top of the brand page. Configure now or skip.")}
                  </p>
                </div>
                <button
                  type="button"
                  className="nb-section__toggle"
                  onClick={() => setBannerOpen((o) => !o)}
                  aria-expanded={bannerOpen}
                  disabled={readOnly}
                >
                  {bannerOpen ? t("Hide") : (
                    <>
                      <Plus size={13} aria-hidden="true" />
                      {t("Add banner")}
                    </>
                  )}
                </button>
              </div>
              {bannerOpen && (
                <div className="nb-section__body">
                  <div className="nb-grid-2">
                    <FormField
                      name="banner_image"
                      label={t("Banner image")}
                      hint={t("Wide image used on the brand page header.")}
                    >
                      <div
                        className={`nb-drop ${values.banner_image ? "has-image" : ""}`}
                        style={{ minHeight: 160, height: 160 }}
                        onClick={() => {
                          if (readOnly) return;
                          const inp = document.getElementById("banner_image_input");
                          inp?.click();
                        }}
                        role="button"
                        tabIndex={readOnly ? -1 : 0}
                      >
                        {values.banner_image ? (
                          <img src={values.banner_image} alt="" className="nb-drop__img" />
                        ) : (
                          <div className="nb-drop__empty">
                            <span className="nb-drop__bubble">
                              <Upload size={16} aria-hidden="true" />
                            </span>
                            <div className="nb-drop__title">{t("Upload banner")}</div>
                          </div>
                        )}
                        <input
                          id="banner_image_input"
                          type="file"
                          accept={ACCEPTED_TYPES}
                          hidden
                          disabled={readOnly}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadBannerField(f, "banner_image");
                            e.target.value = "";
                          }}
                        />
                      </div>
                    </FormField>
                    <FormField
                      name="ar_banner_image"
                      label={t("Banner image (Arabic)")}
                      hint={t("Localized variant for RTL pages.")}
                    >
                      <div
                        className={`nb-drop ${values.ar_banner_image ? "has-image" : ""}`}
                        style={{ minHeight: 160, height: 160 }}
                        onClick={() => {
                          if (readOnly) return;
                          const inp = document.getElementById("ar_banner_image_input");
                          inp?.click();
                        }}
                        role="button"
                        tabIndex={readOnly ? -1 : 0}
                      >
                        {values.ar_banner_image ? (
                          <img src={values.ar_banner_image} alt="" className="nb-drop__img" />
                        ) : (
                          <div className="nb-drop__empty">
                            <span className="nb-drop__bubble">
                              <Upload size={16} aria-hidden="true" />
                            </span>
                            <div className="nb-drop__title">{t("Upload AR banner")}</div>
                          </div>
                        )}
                        <input
                          id="ar_banner_image_input"
                          type="file"
                          accept={ACCEPTED_TYPES}
                          hidden
                          disabled={readOnly}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadBannerField(f, "ar_banner_image");
                            e.target.value = "";
                          }}
                        />
                      </div>
                    </FormField>
                    <FormField name="banner_type" label={t("Banner links to")}>
                      <ReactSelect
                        inputId="banner_type"
                        options={BANNER_TYPE_OPTIONS}
                        styles={nbSelectStyles}
                        placeholder={t("Pick a destination type…")}
                        isDisabled={readOnly}
                        value={
                          BANNER_TYPE_OPTIONS.find(
                            (o) => o.value === values.banner_type,
                          ) || null
                        }
                        onChange={(opt) => {
                          formik.setFieldValue("banner_type", opt?.value || null);
                          formik.setFieldValue("banner_master_id", "");
                        }}
                        menuPortalTarget={document.body}
                      />
                    </FormField>
                    <FormField name="banner_master_id" label={t("Destination")}>
                      <ReactSelect
                        inputId="banner_master_id"
                        options={bannerMasterOptions}
                        styles={nbSelectStyles}
                        placeholder={t("Pick a destination…")}
                        isDisabled={readOnly || !values.banner_type}
                        value={
                          bannerMasterOptions.find(
                            (o) => o.value === values.banner_master_id,
                          ) || null
                        }
                        onChange={(opt) =>
                          formik.setFieldValue(
                            "banner_master_id",
                            opt?.value || "",
                          )
                        }
                        menuPortalTarget={document.body}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 04 Visibility */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">{isEdit ? "04" : "03"}</span>
              <div>
                <h2 className="nb-section__title">{t("Visibility")}</h2>
                <p className="nb-section__hint">
                  {t("Where this brand appears, and in what order.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="status"
                  label={t("Status")}
                  touched={touched.status}
                  error={errors.status}
                >
                  <div className="nb-toggle" role="radiogroup" aria-label={t("Status")}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className={`nb-toggle__opt ${isActive ? "is-on" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.ACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--on" />
                      {t("Active")}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isActive}
                      className={`nb-toggle__opt ${!isActive ? "is-off" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.INACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--off" />
                      {t("Inactive")}
                    </button>
                  </div>
                </FormField>

                <FormField
                  name="position"
                  label={t("Position")}
                  hint={t("Lower numbers appear first.")}
                  touched={touched.position}
                  error={errors.position}
                >
                  <div className="nb-stepper">
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.max(POSITION_MIN, Number(values.position || 1) - 1),
                        )
                      }
                      aria-label={t("Decrease")}
                    >
                      −
                    </button>
                    <input
                      id="position"
                      name="position"
                      type="number"
                      inputMode="numeric"
                      min={POSITION_MIN}
                      max={POSITION_MAX}
                      className="nb-input nb-input--center"
                      value={values.position}
                      onChange={(e) => {
                        const v = e.target.value;
                        formik.setFieldValue("position", v === "" ? "" : Number(v));
                      }}
                      onBlur={formik.handleBlur}
                      disabled={readOnly}
                    />
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.min(POSITION_MAX, Number(values.position || 0) + 1),
                        )
                      }
                      aria-label={t("Increase")}
                    >
                      +
                    </button>
                  </div>
                </FormField>
              </div>

              <label className="nb-switchrow">
                <input
                  id="home_view"
                  name="home_view"
                  type="checkbox"
                  checked={Boolean(values.home_view)}
                  onChange={(e) =>
                    formik.setFieldValue("home_view", e.target.checked)
                  }
                  disabled={readOnly}
                />
                <span className="nb-switchrow__track" aria-hidden="true">
                  <span className="nb-switchrow__thumb" />
                </span>
                <span className="nb-switchrow__text">
                  <strong>{t("Show on home page")}</strong>
                  <span>
                    {t("Surface this brand on the storefront home as a featured shortcut.")}
                  </span>
                </span>
              </label>
            </div>
          </section>

          {/* 05 SEO */}
          <LandingPageSeoSection
            formik={formik}
            readOnly={readOnly}
            step="05"
            kind="brand"
            landingPage={landingPage}
            previewName={previewName}
            previewArName={previewArName}
          />
        </div>

        {/* Right: live preview */}
        <aside className="nb-preview">
          <div className="nb-preview__sticky">
            <div className="nb-preview__head">
              <span className="nb-preview__eyebrow">
                <span className="nb-preview__pulse" aria-hidden="true" />
                {t("Live preview")}
              </span>
              <div className="nb-preview__tabs" role="tablist">
                {PREVIEW_TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={previewTab === key}
                    className={`nb-preview__tab ${previewTab === key ? "is-active" : ""}`}
                    onClick={() => setPreviewTab(key)}
                  >
                    <Icon size={13} aria-hidden="true" />
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="nb-preview__stage">
              {previewTab === "storefront" && (
                <div className="nb-stage nb-stage--storefront">
                  <div className="nb-store-frame">
                    <div className="nb-store-frame__chrome">
                      <span /> <span /> <span />
                      <em>nysmah.com</em>
                    </div>
                    <div className="nb-store-card">
                      <div className={`nb-store-card__media ${imageUrl ? "" : "is-empty"}`}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={previewName} />
                        ) : (
                          <span className="nb-store-card__placeholder">
                            {previewName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="nb-store-card__body">
                        <h3 className="nb-store-card__name">{previewName}</h3>
                        <span className="nb-store-card__cta">
                          {t("Shop the brand")}
                          <ChevronRight size={13} aria-hidden="true" />
                        </span>
                      </div>
                      {!isActive && (
                        <span className="nb-store-card__hidden">
                          {t("Hidden — won't appear on storefront")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "page" && (
                <div className="nb-stage nb-stage--page">
                  <PagePreview
                    path={previewPath}
                    currentPath={landingPage?.path}
                    en={{
                      name: previewName,
                      heading: (values.lp_h1 || "").trim() || previewName,
                      html: values.lp_intro_html,
                    }}
                    ar={{
                      name: previewArName || previewName,
                      heading: (values.lp_ar_h1 || "").trim() || previewArName || previewName,
                      html: values.lp_ar_intro_html,
                    }}
                  />
                </div>
              )}
              {previewTab === "listing" && (
                <div className="nb-stage nb-stage--listing">
                  <div className="nb-listing-frame">
                    <div className="nb-listing-head">
                      <span>{t("Brand")}</span>
                      <span>{t("Status")}</span>
                      <span>{t("Created")}</span>
                    </div>
                    <div className="nb-listing-row">
                      <div className="nb-listing-row__brand">
                        <div className={`nb-listing-avatar ${imageUrl ? "" : "is-empty"}`}>
                          {imageUrl ? (
                            <img src={imageUrl} alt={previewName} />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="nb-listing-row__info">
                          <strong>{previewName}</strong>
                          <span className="nb-listing-row__pos">
                            #{values.position || POSITION_MIN}
                          </span>
                        </div>
                      </div>
                      <div className="nb-listing-row__col">
                        <span
                          className={`nb-badge ${
                            isActive ? "nb-badge--on" : "nb-badge--off"
                          }`}
                        >
                          <span className="nb-badge__dot" aria-hidden="true" />
                          {isActive ? t("Active") : t("Inactive")}
                        </span>
                      </div>
                      <div className="nb-listing-row__col nb-listing-row__col--muted">
                        {isEdit ? t("Last updated · by you") : t("Just now · by you")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "search" && (
                <div className="nb-stage nb-stage--search">
                  <div className="nb-search-frame">
                    <div className="nb-search-head">
                      <Search size={12} aria-hidden="true" />
                      <span>nysmah</span>
                    </div>
                    <div className="nb-search-result">
                      <div className="nb-search-result__url">
                        <span className="nb-search-result__favicon">
                          {imageUrl ? (
                            <img src={imageUrl} alt="" />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </span>
                        <div>
                          <span className="nb-search-result__site">nysmah.com</span>
                          <span className="nb-search-result__path">
                            {crumbPath(previewPath)}
                          </span>
                        </div>
                      </div>
                      <h4 className="nb-search-result__title">{previewMetaTitle}</h4>
                      <p className="nb-search-result__desc">{previewMetaDesc}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nb-preview__caption">
              {previewTab === "storefront" && t("Tile shown on the brand directory.")}
              {previewTab === "page" && t("The page as the shopper sees it, with the score for Google.")}
              {previewTab === "listing" && t("Row shown in the admin Brand List.")}
              {previewTab === "search" && t("Snippet on Google results — uses Meta fields.")}
            </div>
          </div>
        </aside>

        {/* Floating action bar — hidden entirely in read-only mode */}
        {!readOnly && (
          <div className={`nb-actionbar ${isDirty || submitting ? "is-visible" : ""}`}>
            <div className="nb-actionbar__inner">
              <span className="nb-actionbar__status">
                {submitting ? (
                  <>
                    <span className="nb-actionbar__spin" />
                    {isEdit ? t("Saving changes…") : t("Creating brand…")}
                  </>
                ) : errorCount > 0 ? (
                  <>
                    <AlertCircle size={14} aria-hidden="true" />
                    {errorCount === 1
                      ? t("1 error to fix before saving")
                      : t(`${errorCount} errors to fix before saving`)}
                  </>
                ) : (
                  <>
                    <span className="nb-actionbar__dot" aria-hidden="true" />
                    {t("Unsaved changes")}
                  </>
                )}
              </span>
              <div className="nb-actionbar__btns">
                <button
                  type="button"
                  className="nb-btn nb-btn--ghost"
                  onClick={onDiscard}
                  disabled={submitting}
                >
                  {t("Discard")}
                </button>
                <button
                  type="submit"
                  className="nb-btn nb-btn--primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner />
                  ) : isEdit ? (
                    t("Save changes")
                  ) : (
                    t("Create brand")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default BrandForm;
